import React, { useState, useEffect } from 'react';
import { productsApi } from '../../../services/api';
import { Product } from '../../../types';
import toast from 'react-hot-toast';
import Modal from '../../../components/Modal';
import { LoadingState } from '../../../components/LoadingSpinner';
import { usePageTitle } from '../../../hooks/usePageTitle';

// Components
import ProductsPageHeader from './components/ProductsPageHeader';
import ProductsList from './components/ProductsList';
import ProductForm from './components/ProductForm';
import DeleteProductModal from './components/DeleteProductModal';

// Hooks
import { useProductForm } from './hooks/useProductForm';

const ProductsPage: React.FC = () => {
  usePageTitle('Produtos');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    formData,
    formErrors,
    selectedFile,
    imagePreview,
    validateForm: validateProductForm,
    handleInputChange: handleProductInputChange,
    handleFileSelect: handleProductFileSelect,
    handleRemoveImage: handleProductRemoveImage,
    resetForm,
    setEditingProduct: setFormEditingProduct
  } = useProductForm();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await productsApi.getAll({ page: 1, limit: 100 });
      setProducts(response.products);
    } catch (error: any) {
      toast.error('Erro ao carregar produtos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateProductForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      setIsSubmitting(true);
      
      let productId = editingProduct?.id;
      let imageUrl = formData.image_url;

      // Fazer upload da imagem se houver arquivo selecionado
      if (selectedFile) {
        const uploadResponse = await productsApi.uploadImage(selectedFile, productId);
        imageUrl = uploadResponse.imageUrl;
      }

      // Criar ou atualizar produto
      const productData = {
        ...formData,
        image_url: imageUrl
      };

      if (editingProduct) {
        await productsApi.update(editingProduct.id, productData as any);
        toast.success('Produto atualizado com sucesso!');
      } else {
        await productsApi.create(productData as any);
        toast.success('Produto criado com sucesso!');
      }
      
      await loadProducts();
      closeModal();
    } catch (error: any) {
      toast.error(error.error || 'Erro ao salvar produto');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingProduct) return;

    try {
      setIsSubmitting(true);
      await productsApi.delete(deletingProduct.id);
      toast.success('Produto excluído com sucesso!');
      await loadProducts();
      setIsDeleteModalOpen(false);
      setDeletingProduct(null);
    } catch (error: any) {
      toast.error(error.error || 'Erro ao excluir produto');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormEditingProduct(product);
    setIsModalOpen(true);
  };

  const openDeleteModal = (product: Product) => {
    setDeletingProduct(product);
    setIsDeleteModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    resetForm();
  };

  if (loading) {
    return <LoadingState message="Carregando produtos..." />;
  }

  return (
    <div className="space-y-6">
      <ProductsPageHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onCreateClick={openCreateModal}
      />

      <ProductsList
        products={filteredProducts}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Product Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingProduct ? 'Editar Produto' : 'Novo Produto'}
        size="lg"
      >
        <ProductForm
          formData={formData}
          formErrors={formErrors}
          isEditing={!!editingProduct}
          isSubmitting={isSubmitting}
          selectedFile={selectedFile}
          imagePreview={imagePreview}
          onInputChange={handleProductInputChange}
          onFileSelect={handleProductFileSelect}
          onRemoveImage={handleProductRemoveImage}
          onSubmit={handleSubmit}
          onCancel={closeModal}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteProductModal
        isOpen={isDeleteModalOpen}
        product={deletingProduct}
        isSubmitting={isSubmitting}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ProductsPage;
